import type { QueryClient } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { useSupabaseSession } from "~/context/SupabaseSessionContext";
import type { TPractitioner } from "~/database/types";
import { useApiClient } from "~/hooks/use-api-client";
import type { SupabaseClient } from "~/integrations/supabase";
import { supabase } from "~/integrations/supabase/ssr.browser";
import type { TProfile } from "~/integrations/supabase/types";
import type { AuthSessionContext } from "~/modules.server/auth/service.server";

import { API_URL } from "./constants";
import { logger } from "./logger";

export const acceptTerms = async () => {
    const response = await fetch(`${API_URL}/accept-terms`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({}),
    });

    if (!response.ok) {
        throw new Error("Failed to accept terms");
    }

    return await response.json();
};

// Fetch user profile data - can be used on both client and server
export const fetchUserProfile = async (
    supabaseClient: SupabaseClient,
    userId: string,
): Promise<null | (TProfile & { practitioner: TPractitioner | null })> => {
    const { data, error } = await supabaseClient
        .from("profiles")
        .select("*, practitioner!practitioner_profile_id_fkey(*)")
        .eq("id", userId)
        .single();

    if (error) {
        return null;
    }

    return data as unknown as TProfile & {
        practitioner: TPractitioner | null;
    };
};

// Prefetch user profile data for server-side rendering
export const prefetchUserProfile = async (
    queryClient: QueryClient,
    authContext?: AuthSessionContext,
) => {
    if (
        !authContext ||
        authContext.authStatus !== "authenticated" ||
        !authContext.supabaseUserClient
    ) {
        return;
    }

    await queryClient.prefetchQuery({
        queryKey: ["user-profile"],
        queryFn: async () => {
            if (!authContext.userId) {
                return undefined;
            }

            return await fetchUserProfile(
                authContext.supabaseUserClient,
                authContext.userId,
            );
        },
        retry: false,
    });
};

export const useUserProfile = () => {
    const { session, loading: sessionLoading } = useSupabaseSession();

    return useQuery({
        queryKey: ["user-profile"],
        retry: false,
        queryFn: async () => {
            if (!session)
                throw new Error("Can't fetch user profile without session");

            return await fetchUserProfile(supabase, session.user.id);
        },
        enabled: !!session,
        // Data is prefetched on server via _protected.tsx loader
        staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
        initialDataUpdatedAt: 0,
        retryDelay: 1000,
    });
};

export const useAcceptTerms = () => {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async () => {
            const result = await apiClient.post("/accept-terms");

            return result.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["user-profile"] });
        },
    });
};

export const useAccountResearchRelease = () => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    const { userId } = useSupabaseSession();

    // Get user profile data, which includes hasResearchRelease
    const { data: userProfile, isLoading } = useUserProfile();
    const hasResearchRelease = userProfile?.hasResearchRelease ?? false;

    // Mutation to update research release preference
    const { mutate: updateResearchRelease, isPending } = useMutation({
        mutationFn: async (enabled: boolean) => {
            logger
                .withMetadata({ userId, enabled })
                .info("Updating account session donation preference via API");

            const response = await apiClient.put("/account/research-release", {
                hasResearchRelease: enabled,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(
                    errorData.error ||
                        "Failed to update session donation preference",
                );
            }
            // Return the updated value from the API response if needed, or just success
            return await response.json();
        },
        onMutate: async (enabled) => {
            // Cancel any outgoing refetches for the user profile
            await queryClient.cancelQueries({
                queryKey: ["user-profile"],
            });

            // Snapshot the previous profile value
            const previousProfile = queryClient.getQueryData(["user-profile"]);

            // Optimistically update the user profile data in the cache
            queryClient.setQueryData(["user-profile"], (old: any) => {
                if (!old) return old;
                return { ...old, hasResearchRelease: enabled };
            });

            return { previousProfile }; // Return context with previous profile
        },
        onError: (error, enabled, context) => {
            // Log the error
            logger
                .withError(error)
                .withMetadata({ userId, enabled })
                .error("Failed to update session donation preference");

            // Show error toast
            toast.error("Failed to update session donation preference");

            // Revert the optimistic update using the context
            if (context?.previousProfile) {
                queryClient.setQueryData(
                    ["user-profile"],
                    context.previousProfile,
                );
            } else {
                // If no context, invalidate to refetch
                queryClient.invalidateQueries({ queryKey: ["user-profile"] });
            }
        },
        onSuccess: (data, enabled) => {
            // Show success toast
            toast.success(
                `Session donation ${
                    enabled ? "enabled" : "disabled"
                } for your account`,
            );

            // Invalidate the user-profile query to ensure consistency after mutation
            queryClient.invalidateQueries({
                queryKey: ["user-profile"],
            });
        },
    });

    return {
        hasResearchRelease,
        updateResearchRelease,
        isLoading,
        isPending,
    };
};

export function useIsPractitioner() {
    const {
        data: profile,
        isLoading: isLoadingProfile,
        refetch,
    } = useUserProfile();

    // Derived state variables
    const isPractitioner = !!profile?.practitioner;
    const hasBaaSigned = !!profile?.practitioner?.baaAcceptedAt;
    const needsBaaSigning = isPractitioner && !hasBaaSigned;

    return {
        isPractitioner,
        hasBaaSigned,
        needsBaaSigning,
        isLoading: isLoadingProfile,
        refetch,
    };
}

export const useUserFeatureFlags = () => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    const { userId } = useSupabaseSession();

    const { data: userProfile, isLoading } = useUserProfile();
    const featureFlags =
        (userProfile?.featureFlags as Record<string, any>) || {};

    const { mutate: updateFeatureFlags, isPending } = useMutation({
        mutationFn: async ({
            flagName,
            value,
        }: {
            flagName: string;
            value: boolean;
        }) => {
            logger
                .withMetadata({ userId, flagName, value })
                .info("Updating feature flag via API");

            const response = await apiClient.put("/account/feature-flags", {
                [flagName]: value,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(
                    errorData.error || "Failed to update feature flags",
                );
            }

            return await response.json();
        },
        onMutate: async ({ flagName, value }) => {
            await queryClient.cancelQueries({ queryKey: ["user-profile"] });

            const previousProfile = queryClient.getQueryData(["user-profile"]);

            queryClient.setQueryData(["user-profile"], (old: any) => {
                if (!old) return old;
                return {
                    ...old,
                    featureFlags: {
                        ...(old.featureFlags || {}),
                        [flagName]: value,
                    },
                };
            });

            return { previousProfile };
        },
        onError: (error, { flagName, value }, context) => {
            logger
                .withError(error)
                .withMetadata({ userId, flagName, value })
                .error("Failed to update feature flags");

            toast.error("Failed to update feature flags");

            if (context?.previousProfile) {
                queryClient.setQueryData(
                    ["user-profile"],
                    context.previousProfile,
                );
            } else {
                queryClient.invalidateQueries({ queryKey: ["user-profile"] });
            }
        },
        onSuccess: (data, { flagName, value }) => {
            toast.success(
                `Feature flag '${flagName}' ${
                    value ? "enabled" : "disabled"
                } for your account`,
            );
            queryClient.invalidateQueries({ queryKey: ["user-profile"] });
        },
    });

    return {
        featureFlags,
        updateFeatureFlags: (flagName: string, value: boolean) =>
            updateFeatureFlags({ flagName, value }),
        isLoading,
        isPending,
    };
};
